import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, createContext, useContext } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Box } from '../Box/index.js';
import { getDirectionalStyles, getStatusIcon, horizontalStyles } from './styles.js';

const ProgressListContext = /*#__PURE__*/createContext({});
const useProgressListContext = () => useContext(ProgressListContext);
/* ======================================= */
const StyledProgressListItem = styled(Box).withConfig({
  displayName: "ProgressList__StyledProgressListItem",
  componentId: "sc-28fuh-0"
})(["&:not(:last-child){padding-bottom:", ";&:before{content:' ';position:absolute;height:100%;border-color:", ";", "}}"], _ref => {
  let {
    spacing,
    direction
  } = _ref;
  return direction !== 'horizontal' && themeGet(`space.${spacing}`);
}, themeGet('colors.mist100'), _ref2 => {
  let {
    status,
    direction
  } = _ref2;
  return getDirectionalStyles({
    status,
    direction
  });
});
/* ======================================= */
/**
 * ProgressList provides context to each ProgressItem. Its purpose is to guide
 * users through the steps of a task. It is useful when a given task is
 * complicated or has a certain sequence that we can decompose it into
 * several steps to make things easier.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/progress-list#usage)
 * - [Props](https://blockparty.endpoint.com/components/progress-list#props)
 * <br />
 *
 */
const ProgressList = /*#__PURE__*/forwardRef((_ref3, forwardedRef) => {
  let {
    children,
    spacing = 'space60',
    direction = 'vertical',
    dataTestId = 'progress-list',
    ...props
  } = _ref3;
  const context = {
    direction,
    spacing
  };
  return /*#__PURE__*/React__default.createElement(ProgressListContext.Provider, {
    value: context
  }, /*#__PURE__*/React__default.createElement(Box, _extends({
    ref: forwardedRef,
    "data-test-id": dataTestId,
    display: direction === 'horizontal' ? 'flex' : null,
    role: "list"
  }, props), children));
});
/**
 * ProgressListItem is a single item used in ProgressList.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/progress-list#usage)
 * - [Props](https://blockparty.endpoint.com/components/progress-list#props)
 * <br />
 *
 */
const ProgressListItem = /*#__PURE__*/forwardRef((_ref4, forwardedRef) => {
  let {
    children,
    status,
    dataTestId = 'progress-list-item',
    ...rest
  } = _ref4;
  const {
    direction,
    spacing
  } = useProgressListContext();
  const isHorizontal = direction === 'horizontal';
  const {
    itemsStyles,
    childrenStyles,
    iconContainerStyles
  } = horizontalStyles;
  return /*#__PURE__*/React__default.createElement(StyledProgressListItem, _extends({
    ref: forwardedRef,
    "data-test-id": dataTestId,
    direction: direction,
    display: "flex",
    position: "relative",
    role: "listitem",
    spacing: spacing,
    status: status
  }, isHorizontal && itemsStyles, rest), /*#__PURE__*/React__default.createElement(Box, _extends({
    zIndex: 10
  }, isHorizontal && iconContainerStyles), /*#__PURE__*/React__default.createElement(Box, {
    bg: "white",
    display: "inline-flex"
  }, getStatusIcon(status))), children && /*#__PURE__*/React__default.createElement(Box, _extends({
    ml: "space40",
    position: "relative",
    top: -4
  }, isHorizontal && childrenStyles), children));
});

export { ProgressList, ProgressListItem };
